import { ORDER_ASIN_CONVERSION_TRACKING_DOMAIN_OPTIONS, ORDER_GOAL_KPI_OPTIONS_BY_GOAL } from "./constants";

// Order form utils

// Takes in raw data from API and processes for form state
export const formatOrderFormData = (data) => {
  const {
    name,
    state,
    delivery_status: deliveryStatus,
    purchase_order_number: purchaseOrderNumber,
    optimization,
    frequencies,
    flights,
    budget_caps: budgetCaps,
    comments,
    fees,
    conversion_tracking_products,
  } = data;

  const formattedFormData = {
    name,
    state,
    deliveryStatus,
    purchaseOrderNumber,
    optimization,
    frequencies,
    frequencyCapped: frequencies?.[0]?.frequencyType !== "UNCAPPED",
    flights,
    budgetCaps: budgetCaps ?? [],
    budgetCapsEnabled: !!budgetCaps?.length,
    comments,
    fees,
    agencyFeeEnabled: fees.agencyFeePercentage > 0,
    conversionTrackingAsins: conversion_tracking_products ?? [],
    conversionTrackingDomain: ORDER_ASIN_CONVERSION_TRACKING_DOMAIN_OPTIONS[0].value,
  };

  return formattedFormData;
};

export const validateMandatoryOrderFormFields = (formState) => {
  const { name, flights, frequencies, optimization: { bidStrategy, goalSetting } = {} } = formState ?? {};

  if (!name || !flights?.length || !frequencies?.length || !bidStrategy || !goalSetting?.kpi) {
    return false;
  }

  return true;
};

// Resolves dependent state where needed in the order form
export const resolveOrderFormFieldDependencies = (formState, budgetCapFields, setValue) => {
  // When all budget caps are cleared, reset enabled state
  if (budgetCapFields?.length === 0 && formState.budgetCapsEnabled) {
    setValue("budgetCapsEnabled", false);
  }

  // When order goal changes, clear out KPI and kpiTarget if no longer applicable to goal
  if (
    formState?.optimization?.goalSetting?.kpi &&
    formState?.optimization?.goalSetting?.goal &&
    !ORDER_GOAL_KPI_OPTIONS_BY_GOAL[formState?.optimization?.goalSetting?.goal]
      ?.map((kpi) => kpi.value)
      ?.includes(formState.optimization?.goalSetting.kpi)
  ) {
    setValue(
      "optimization.goalSetting.kpi",
      ORDER_GOAL_KPI_OPTIONS_BY_GOAL[formState.optimization?.goalSetting?.goal]?.[0]?.value,
    );
  }
};

// Line item form utils
// Extracts targets from API response and converts them to compatible format for react-hook-form state

const convertTargetDataFields = (targetData) => {
  const groupedByTargetDetails = targetData.reduce((acc, item) => {
    const targetDetailsKeys = Object.keys(item.target_details || {});
    if (targetDetailsKeys.length > 0) {
      const groupKey = targetDetailsKeys[0];
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
    }
    return acc;
  }, {});

  const selectTargets = (targets, selector, isNegative = false) =>
    targets?.filter((t) => (isNegative ? t.negative : !t.negative))?.map((t) => selector(t)) ?? [];

  const convertedTargetData = {
    geoTargetIds: selectTargets(
      groupedByTargetDetails?.locationTarget,
      (t) => t.target_details?.locationTarget?.geoLocation,
    ),
    excludedGeoTargetIds: selectTargets(
      groupedByTargetDetails?.locationTarget,
      (t) => t.target_details?.locationTarget?.geoLocation,
      true,
    ),
    appTargetIds: selectTargets(groupedByTargetDetails?.appTarget, (t) => t.target_details?.appTarget?.appId),
    excludedAppTargetIds: selectTargets(
      groupedByTargetDetails?.appTarget,
      (t) => t.target_details?.appTarget?.appId,
      true,
    ),
    audienceGroupTargets: selectTargets(
      groupedByTargetDetails?.audienceTarget,
      (t) => t.target_details?.audienceTarget,
    ),

    audienceGroupExclusions: selectTargets(
      groupedByTargetDetails?.audienceTarget,
      (t) => t.target_details?.audienceTarget,
      true,
    ),

    dayPartTargets: groupedByTargetDetails?.dayPartTarget ?? [],
    deviceTargets: groupedByTargetDetails?.deviceTarget ?? [],
    autoTargets: groupedByTargetDetails?.autoTarget ?? [],
    iabCategoryTargets: selectTargets(
      groupedByTargetDetails?.iabCategoryTarget,
      (t) => t.target_details?.iabCategoryTarget?.iabContentCategory,
    ),
    excludedIabCategoryTargets: selectTargets(
      groupedByTargetDetails?.iabCategoryTarget,
      (t) => t.target_details?.iabCategoryTarget?.iabContentCategory,
      true,
    ),
    targetDomains: selectTargets(
      groupedByTargetDetails?.domainTarget,
      (t) => t.target_details?.domainTarget?.domainTargetDetails?.domainName?.domainName,
    ),

    excludedTargetDomains: selectTargets(
      groupedByTargetDetails?.domainTarget,
      (t) => t.target_details?.domainTarget?.domainTargetDetails?.domainName?.domainName,
      true,
    ),
    contextualProductTargets: selectTargets(
      groupedByTargetDetails?.productTarget,
      (t) => t.target_details?.productTarget?.asin,
    ),
    excludedContextualProductTargets: selectTargets(
      groupedByTargetDetails?.productTarget,
      (t) => t.target_details?.productTarget?.asin,
      true,
    ),
    contextualCategoryTargets: selectTargets(
      groupedByTargetDetails?.productCategoryTarget,
      (t) => t.target_details?.productCategoryTarget?.asinCategory,
    ),

    excludedContextualCategoryTargets: selectTargets(
      groupedByTargetDetails?.productCategoryTarget,
      (t) => t.target_details?.productCategoryTarget?.asinCategory,
      true,
    ),
    contextualKeywordTargets: selectTargets(
      groupedByTargetDetails?.keywordTarget,
      (t) => t.target_details?.keywordTarget?.keyword,
    ),
    amazonInventories: groupedByTargetDetails?.inventorySourceTarget
      ?.filter((t) => t.target_details?.inventorySourceTarget?.inventorySourceType === "AMAZON")
      ?.map((t) => t.target_details?.inventorySourceTarget?.inventorySourceId),
    thirdPartyInventories: groupedByTargetDetails?.inventorySourceTarget
      ?.filter((t) => t.target_details?.inventorySourceTarget?.inventorySourceType === "THIRD_PARTY_EXCHANGE")
      ?.map((t) => t.target_details?.inventorySourceTarget?.inventorySourceId),
    amazonPublisherDirectInventories: groupedByTargetDetails?.inventorySourceTarget
      ?.filter((t) => t.target_details?.inventorySourceTarget?.inventorySourceType === "APD")
      ?.map((t) => t.target_details?.inventorySourceTarget?.inventorySourceId),
  };

  return convertedTargetData;
};

// Takes in raw data from API and processes for form state
export const formatLineItemFormData = (data) => {
  const {
    name,
    state,
    bid,
    optimization,
    frequencies,
    pacing,
    budget_amount: budgetAmount,
    start_date_time: startDateTime,
    end_date_time: endDateTime,
    budget_caps: budgetCaps,
    creative_rotation_type: creativeRotationType,
    advertised_product_category_ids: advProductCategoryIds,
    targeting_settings: targetingSettings,
    inventory_type: inventoryType,
    comments,
    targets,
    creativeAssociations,
  } = data;

  const formattedCreativeAssociations = creativeAssociations?.map((creative) => {
    const { ad_creative_id, weight, state } = creative;
    return {
      ad_creative_id,
      weight,
      state,
    };
  });

  if (bid.baseBid) {
    bid.baseBid = parseFloat(bid.baseBid).toFixed(2);
  }

  if (bid.maxAverageCPM) {
    bid.maxAverageCPM = parseFloat(bid.maxAverageCPM).toFixed(2);
  }
  const {
    geoTargetIds,
    excludedGeoTargetIds,
    appTargetIds,
    excludedAppTargetIds,
    audienceGroupTargets,
    audienceGroupExclusions,
    excludedAudienceTargetIds,
    dayPartTargets,
    deviceTargets,
    autoTargets,
    iabCategoryTargets,
    excludedIabCategoryTargets,
    targetDomains,
    excludedTargetDomains,
    contextualProductTargets,
    excludedContextualProductTargets,
    contextualCategoryTargets,
    excludedContextualCategoryTargets,
    contextualKeywordTargets,
    amazonInventories,
    thirdPartyInventories,
    amazonPublisherDirectInventories,
  } = convertTargetDataFields(targets ?? {});

  let deviceOrientationTargetValue;
  let deviceMobileOSTargetValue;
  let enableRelatedProductsTargeting = false;

  // Media device type and mobile environment
  const deviceMediaTypeTargets = [
    ...new Set(deviceTargets?.map((target) => target.target_details.deviceTarget.deviceType).filter(Boolean)),
  ];
  const mobileEnvironmentTargets = [
    ...new Set(deviceTargets?.map((target) => target.target_details.deviceTarget.mobileEnvironment).filter(Boolean)),
  ];

  const deviceOrientationTargets = new Set(
    deviceTargets?.map((target) => target.target_details.deviceTarget?.deviceOrientation).filter(Boolean),
  );
  const deviceMobileOSTargets = new Set(
    deviceTargets?.map((target) => target.target_details.deviceTarget?.mobileOS).filter(Boolean),
  );

  // Device orientation targets
  if (deviceOrientationTargets.has("PORTRAIT") && deviceOrientationTargets.has("LANDSCAPE")) {
    deviceOrientationTargetValue = "ALL";
  } else if (deviceOrientationTargets.has("PORTRAIT")) {
    deviceOrientationTargetValue = "PORTRAIT";
  } else if (deviceOrientationTargets.has("LANDSCAPE")) {
    deviceOrientationTargetValue = "LANDSCAPE";
  } else {
    deviceOrientationTargetValue = "ALL";
  }

  // Mobile device targets
  const deviceTypeTargets = deviceTargets
    ?.map((target) => target.target_details?.deviceTarget?.mobileDevice)
    .filter(Boolean);

  // Mobile OS targets
  if (deviceMobileOSTargets.has("IOS") && deviceMobileOSTargets.has("ANDROID")) {
    deviceMobileOSTargetValue = "ALL";
  } else if (deviceMobileOSTargets.has("IOS")) {
    deviceMobileOSTargetValue = "IOS";
  } else if (deviceMobileOSTargets.has("ANDROID")) {
    deviceMobileOSTargetValue = "ANDROID";
  } else {
    deviceMobileOSTargetValue = "ALL";
  }

  // Contextual related ASINs target
  if (autoTargets?.find((t) => t.target_details?.autoTarget?.matchType === "ASIN_RELATED")) {
    enableRelatedProductsTargeting = true;
  }

  const formattedFormData = {
    bid,
    name,
    state,
    optimizeMaxAverageCPM: !bid.maxAverageCPM,
    optimization,
    frequencies,
    frequencyCapped: frequencies?.[0]?.frequencyType !== "UNCAPPED",
    pacing,
    budgetAmount,
    startDateTime,
    endDateTime,
    budgetCaps: budgetCaps ?? [],
    budgetCapsEnabled: !!budgetCaps?.length,
    creativeRotationType,
    advProductCategoryIds,
    targetingSettings,
    inventoryType,
    comments,
    geoTargetIds,
    excludedGeoTargetIds,
    dayPartTargets,
    appTargetIds,
    excludedAppTargetIds,
    audienceGroupTargets,
    audienceGroupExclusions,
    excludedAudienceTargetIds,
    deviceTargets: deviceTypeTargets?.length ? deviceTypeTargets : ["ALL"],
    deviceOrientationTarget: deviceOrientationTargetValue,
    mobileOperatingSystem: deviceMobileOSTargetValue,
    enableRelatedProductsTargeting,
    iabCategoryTargetIds: iabCategoryTargets,
    excludedIabCategoryTargetIds: excludedIabCategoryTargets,
    targetDomains,
    excludedTargetDomains,
    contextualProductTargetIds: contextualProductTargets,
    excludedContextualProductTargetIds: excludedContextualProductTargets,
    contextualCategoryTargetIds: contextualCategoryTargets,
    excludedContextualCategoryTargetIds: excludedContextualCategoryTargets,
    contextualKeywords: contextualKeywordTargets,
    mediaDevices: deviceMediaTypeTargets,
    mediaMobileEnvironments: mobileEnvironmentTargets,
    amazonInventories,
    thirdPartyInventories,
    amazonPublisherDirectInventories,
    creativeAssociations: formattedCreativeAssociations,
  };

  return formattedFormData;
};

export const validateMandatoryLineItemFormFields = (formState) => {
  const {
    name,
    bid,
    frequencies,
    optimization,
    inventoryType,
    creativeRotationType,
    startDateTime,
    endDateTime,
    pacing,
    targetingSettings,
    advProductCategoryIds,
    budgetAmount,
  } = formState ?? {};

  if (
    !name ||
    !inventoryType ||
    !creativeRotationType ||
    !startDateTime ||
    !endDateTime ||
    !frequencies?.length ||
    !advProductCategoryIds?.length ||
    !optimization?.bidStrategy ||
    !pacing.deliveryProfile ||
    !targetingSettings?.amazonViewability?.viewabilityTier ||
    (!"includeUnmeasurableImpressions") in targetingSettings ||
    !targetingSettings?.timeZoneType ||
    !targetingSettings?.userLocation ||
    !targetingSettings?.userLocationSignal ||
    !optimization?.bidStrategy ||
    !bid.baseBid ||
    (!optimization?.automateBudgetAllocation && !budgetAmount)
  ) {
    return false;
  }

  return true;
};

// Resolves dependent state where needed in the line item form
export const resolveLineItemFormFieldDependencies = (formState, budgetCapFields, setValue) => {
  if (budgetCapFields?.length === 0 && formState.budgetCapsEnabled) {
    setValue("budgetCapsEnabled", false);
  }

  if (
    formState.targetingSettings?.amazonViewability?.viewabilityTier === "ALL_TIERS" &&
    formState.targetingSettings?.amazonViewability?.includeUnmeasurableImpressions
  ) {
    setValue("targetingSettings.amazonViewability.includeUnmeasurableImpressions", false);
  }

  if (formState.optimizeMaxAverageCPM && formState.bid.maxAverageCPM) {
    setValue('"bid', { ...formState.bid, maxAverageCPM: "" }, { shouldDirty: true });
  }
};

export const castNumbersInFormData = (formData) => {
  if (typeof formData === "object" && formData !== null) {
    const keys = Object.keys(formData);

    for (const key of keys) {
      if (["advertised_product_category_ids", "advertiser_id", "flightId"].includes(key)) continue;
      formData[key] = castNumbersInFormData(formData[key]);
    }
  } else if (typeof formData === "string" && !isNaN(formData)) {
    return Number(formData);
  }

  return formData;
};
